import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import {
    getSliders,
    createSlider,
    updateSlider,
    deleteSlider,
    Slider
} from '../services/sliderService';
import '../styles/pages.css';

// Slider Card Component
interface SliderCardProps {
    slider: Slider;
    onEdit: (slider: Slider) => void;
    onDelete: (id: string) => void;
    onClick: (slider: Slider) => void;
    t: any;
}

const SliderCard: React.FC<SliderCardProps> = ({ slider, onEdit, onDelete, onClick, t }) => {
    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit(slider);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(slider.id);
    };

    return (
        <div className="card card--padding-md card--shadow-md card--border slider-card">
            <div className="slider-order-badge">
                #{slider.order}
            </div>
            <img
                src={slider.image || '/api/placeholder/300/200'}
                alt={`Slider ${slider.order}`}
                className="slider-card-image"
                onError={(e) => {
                    e.currentTarget.src = '/api/placeholder/300/200';
                }}
            />
            <div className="slider-card-content">
                <p className="slider-order-text">
                    {t('sliders.order')}: {slider.order}
                </p>
                <div className="slider-card-actions">
                    <Button
                        onClick={handleEdit}
                        className="btn-secondary"
                    >
                        {t('common.edit')}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        className="btn-danger"
                    >
                        {t('common.delete')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Add/Edit Slider Modal Component
interface SliderModalProps {
    slider?: Slider;
    isOpen: boolean;
    onClose: () => void;
    onSave: (slider: Omit<Slider, 'id' | 'createdAt' | 'updatedAt'>, file?: File) => void;
    t: any;
    nextAvailableOrder?: number;
}

const SliderModal: React.FC<SliderModalProps> = ({ slider, isOpen, onClose, onSave, t, nextAvailableOrder }) => {
    const [formData, setFormData] = useState({
        image: '',
        order: 1,
        isActive: true
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        if (slider) {
            setFormData({
                image: slider.image,
                order: slider.order,
                isActive: slider.isActive
            });
            setImagePreview(slider.image);
            setSelectedFile(null); // Don't allow changing photo when editing (would need separate upload)
        } else {
            setFormData({
                image: '',
                order: nextAvailableOrder || 1,
                isActive: true
            });
            setImagePreview(null);
            setSelectedFile(null);
        }
        setErrors({});
    }, [slider, isOpen, nextAvailableOrder]);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // For new sliders, file is required. For editing, existing image is fine
        if (!slider && !selectedFile) {
            newErrors.image = t('forms.required');
        }
        if (formData.order < 0) {
            newErrors.order = t('forms.minValue', { min: 0 });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            onSave({
                image: formData.image,
                order: formData.order,
                isActive: formData.isActive
            }, selectedFile || undefined);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setErrors(prev => ({
                    ...prev,
                    image: 'File size must be less than 5MB'
                }));
                return;
            }

            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({
                    ...prev,
                    image: 'Please select an image file'
                }));
                return;
            }

            // Store the file for API upload
            setSelectedFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (event) => {
                const result = event.target?.result as string;
                setImagePreview(result);
                handleInputChange('image', result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview(null);
        setSelectedFile(null);
        handleInputChange('image', '');
        // Reset file input
        const fileInput = document.getElementById('image-upload') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-content--medium" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2 className="modal-title">
                        {slider ? t('sliders.editSlider') : t('sliders.addSlider')}
                    </h2>
                    <button className="modal-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label className="form-label">
                            {t('sliders.image')} *
                        </label>

                        <div className="image-upload-container">
                            {imagePreview ? (
                                <div className="image-preview">
                                    <img src={imagePreview} alt="Preview" />
                                    <div className="image-preview-overlay">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleRemoveImage}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3,6 5,6 21,6" />
                                                <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                            </svg>
                                            {t('common.delete')}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="image-upload-placeholder">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                        <polyline points="21,15 16,10 5,21" />
                                    </svg>
                                    <p>{t('sliders.uploadImage')}</p>
                                </div>
                            )}

                            <input
                                id="image-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                            />

                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => document.getElementById('image-upload')?.click()}
                                className="upload-button"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7,10 12,15 17,10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                {imagePreview ? t('common.change') : t('sliders.uploadImage')}
                            </Button>
                        </div>

                        {errors.image && (
                            <div className="form-error">{errors.image}</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            {t('sliders.order')} *
                        </label>
                        <Input
                            type="number"
                            value={formData.order}
                            onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
                            error={errors.order}
                            min={0}
                            className="input-field"
                        />
                        {!slider && (
                            <small style={{
                                color: 'var(--text-muted)',
                                fontSize: 'var(--font-size-xs)',
                                marginTop: '0.5rem',
                                display: 'block'
                            }}>
                                {t('sliders.autoAssignedOrder')}
                            </small>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            {t('sliders.isActive') || 'Active Status'}
                        </label>
                        <select
                            value={formData.isActive ? 'true' : 'false'}
                            onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                            className="form-select"
                        >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>

                    <div className="modal-actions">
                        <Button type="button" variant="secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" variant="primary">
                            {t('common.save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Main Sliders Component
const Sliders: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { addToast } = useToast();
    const [sliders, setSliders] = useState<Slider[]>([]);
    const [filteredSliders, setFilteredSliders] = useState<Slider[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSlider, setEditingSlider] = useState<Slider | undefined>();
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Load sliders from API
    useEffect(() => {
        const loadSliders = async () => {
            try {
                setLoading(true);
                const data = await getSliders();
                setSliders(data);
            } catch (error: any) {
                console.error('Failed to load sliders:', error);
                addToast({
                    title: 'Error',
                    message: error?.message || 'Failed to load sliders. Please try again.',
                    type: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadSliders();
    }, [addToast]);

    useEffect(() => {
        let filtered = sliders;

        // Filter by search term - for simplified sliders, we can search by order or ID
        if (searchTerm) {
            filtered = filtered.filter(slider =>
                slider.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                slider.order.toString().includes(searchTerm)
            );
        }

        // Sort by order
        filtered.sort((a, b) => a.order - b.order);

        setFilteredSliders(filtered);
    }, [sliders, searchTerm, i18n.language]);

    const handleAddSlider = () => {
        setEditingSlider(undefined);
        setIsModalOpen(true);
    };

    const handleEditSlider = (slider: Slider) => {
        setEditingSlider(slider);
        setIsModalOpen(true);
    };

    const handleSaveSlider = async (sliderData: Omit<Slider, 'id' | 'createdAt' | 'updatedAt'>, file?: File) => {
        try {
            if (editingSlider) {
                // Update existing slider
                await updateSlider(editingSlider.id, {
                    display_order: sliderData.order,
                    is_active: sliderData.isActive !== undefined ? sliderData.isActive : true
                });

                // Refresh sliders list
                const data = await getSliders();
                setSliders(data);

                addToast({ title: t('notifications.success'), message: t('sliders.sliderUpdated'), type: 'success' });
            } else {
                // Create new slider - file is required
                if (!file) {
                    addToast({ 
                        title: 'Error', 
                        message: 'Please select an image file', 
                        type: 'error' 
                    });
                    return;
                }

                await createSlider({
                    photo: file,
                    is_active: sliderData.isActive !== undefined ? sliderData.isActive : true,
                    display_order: sliderData.order
                });

                // Refresh sliders list
                const data = await getSliders();
                setSliders(data);

                addToast({ title: t('notifications.success'), message: t('sliders.sliderAdded'), type: 'success' });
            }
            setIsModalOpen(false);
        } catch (error: any) {
            console.error('Failed to save slider:', error);
            addToast({
                title: 'Error',
                message: error?.message || 'Failed to save slider. Please try again.',
                type: 'error'
            });
        }
    };

    // Helper function to get next available order number
    const getNextAvailableOrder = () => {
        if (sliders.length === 0) return 1;

        // Get all existing order numbers and sort them
        const existingOrders = sliders.map(slider => slider.order).sort((a, b) => a - b);

        // Find the first gap in the sequence or return next number
        for (let i = 1; i <= existingOrders.length + 1; i++) {
            if (!existingOrders.includes(i)) {
                return i;
            }
        }

        // If no gaps, return next number in sequence
        return existingOrders.length + 1;
    };

    // Helper function to reorder sliders after deletion
    const reorderSliders = (slidersToReorder: Slider[]) => {
        return slidersToReorder
            .sort((a, b) => a.order - b.order)
            .map((slider, index) => ({
                ...slider,
                order: index + 1
            }));
    };

    const handleDeleteSlider = async (id: string) => {
        if (deleteConfirm === id) {
            try {
                await deleteSlider(id);
                
                // Refresh sliders list
                const data = await getSliders();
                setSliders(data);

                addToast({ title: t('notifications.success'), message: t('sliders.sliderDeleted'), type: 'success' });
                setDeleteConfirm(null);
            } catch (error: any) {
                console.error('Failed to delete slider:', error);
                addToast({
                    title: 'Error',
                    message: error?.message || 'Failed to delete slider. Please try again.',
                    type: 'error'
                });
            }
        } else {
            setDeleteConfirm(id);
            // Auto-clear confirmation after 3 seconds
            setTimeout(() => setDeleteConfirm(null), 3000);
        }
    };

    const handleSliderClick = (slider: Slider) => {
        // Sliders are now just images, no click action needed
        // You can add custom click behavior here if needed
    };

    if (loading) {
        return (
            <div className="page-container">
                <div className="page-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container sliders-page">
            <div className="page-header">
                <div className="page-title-section">
                    <h1 className="page-title">{t('sliders.title')}</h1>
                    <p className="page-subtitle">
                        {filteredSliders.length}*
                    </p>
                </div>
                <div className="page-actions">
                    <Button onClick={handleAddSlider} className="btn-primary">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        {t('sliders.addSlider')}
                    </Button>
                </div>
            </div>

            {filteredSliders.length === 0 ? (
                <div className="page-empty">
                    <div className="empty-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <rect x="2" y="3" width="20" height="14" rx="2" />
                            <line x1="8" y1="21" x2="16" y2="21" />
                            <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                    </div>
                    <h3 className="empty-title">{t('sliders.noSliders')}</h3>
                    <p className="empty-description">
                        {searchTerm
                            ? t('common.noResults')
                            : t('sliders.noSliders')
                        }
                    </p>
                    {!searchTerm && (
                        <Button onClick={handleAddSlider} className="btn-primary">
                            {t('sliders.addSlider')}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="page-grid">
                    {filteredSliders.map((slider) => (
                        <SliderCard
                            key={slider.id}
                            slider={slider}
                            onEdit={handleEditSlider}
                            onDelete={handleDeleteSlider}
                            onClick={handleSliderClick}
                            t={t}
                        />
                    ))}
                </div>
            )}

            <SliderModal
                slider={editingSlider}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveSlider}
                nextAvailableOrder={getNextAvailableOrder()}
                t={t}
            />

            {deleteConfirm && (
                <div className="delete-confirm-overlay">
                    <div className="delete-confirm-dialog">
                        <h3>{t('sliders.deleteConfirm')}</h3>
                        <div className="delete-confirm-actions">
                            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="error" onClick={() => handleDeleteSlider(deleteConfirm)}>
                                {t('common.delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sliders;